import '../config/loadEnv.js'

import type { Firestore, Timestamp as FirestoreTimestamp, FieldValue as FirestoreFieldValue } from 'firebase-admin/firestore'
import type { Auth, DecodedIdToken } from 'firebase-admin/auth'
import type { Storage } from 'firebase-admin/storage'
import type { Database } from 'firebase-admin/database'
import { existsSync, readFileSync } from 'node:fs'
import { resolve } from 'node:path'
import { createLogger } from '@/common/utils/logger'

let initialized = false
let admin: typeof import('firebase-admin') | undefined

let dbInstance: Firestore | undefined
let authInstance: Auth | undefined
let storageInstance: Storage | undefined
let realtimeDbInstance: Database | undefined
let timestampClass: typeof FirestoreTimestamp | undefined
let fieldValueClass: typeof FirestoreFieldValue | undefined

const logger = createLogger('Firebase')

function ensureInitialized() {
  if (!initialized || !dbInstance || !authInstance || !storageInstance || !realtimeDbInstance) {
    throw new Error('Firebase has not been initialized. Call initializeFirebase() first.')
  }
}

const dbProxy = new Proxy({} as Firestore, {
  get(_target, prop, receiver) {
    ensureInitialized()
    const value = Reflect.get(dbInstance!, prop, receiver)
    if (typeof value === 'function') {
      return value.bind(dbInstance)
    }
    return value
  }
})

const authProxy = new Proxy({} as Auth, {
  get(_target, prop, receiver) {
    ensureInitialized()
    const value = Reflect.get(authInstance!, prop, receiver)
    if (typeof value === 'function') {
      return value.bind(authInstance)
    }
    return value
  }
})

const storageProxy = new Proxy({} as Storage, {
  get(_target, prop, receiver) {
    ensureInitialized()
    const value = Reflect.get(storageInstance!, prop, receiver)
    if (typeof value === 'function') {
      return value.bind(storageInstance)
    }
    return value
  }
})

const realtimeDbProxy = new Proxy({} as Database, {
  get(_target, prop, receiver) {
    ensureInitialized()
    const value = Reflect.get(realtimeDbInstance!, prop, receiver)
    if (typeof value === 'function') {
      return value.bind(realtimeDbInstance)
    }
    return value
  }
})

export const db = dbProxy
export const auth = authProxy
export const storage = storageProxy
export const realtimeDb = realtimeDbProxy

function assertDatabaseUrl(value?: string): string {
  if (!value) {
    throw new Error("Can't determine Firebase Database URL.")
  }
  return value
}

export async function initializeFirebase() {
  if (initialized) return

  // Lazy-load firebase-admin
  const adminModule = await import('firebase-admin')
  admin = adminModule.default || adminModule

  const isDev = process.env.NODE_ENV === 'development'
  const serviceAccountPath = resolve(process.cwd(), 'serviceAccount.json')
  const monorepoRootServiceAccountPath = resolve(process.cwd(), '../serviceAccount.json')
  const envServiceAccountPath = process.env.FIREBASE_SERVICE_ACCOUNT_PATH
    ? resolve(process.env.FIREBASE_SERVICE_ACCOUNT_PATH)
    : null
  const hasEnvServiceAccount = envServiceAccountPath ? existsSync(envServiceAccountPath) : false
  const hasServiceAccount = existsSync(serviceAccountPath) || existsSync(monorepoRootServiceAccountPath)
  const resolveDevServiceAccountPath = (): string => {
    if (existsSync(serviceAccountPath)) return serviceAccountPath
    if (existsSync(monorepoRootServiceAccountPath)) return monorepoRootServiceAccountPath
    throw new Error('serviceAccount.json not found in api/ or repository root')
  }

  try {
    // Strategy 0: Explicit service account path from env (works in prod too)
    if (hasEnvServiceAccount) {
      logger.info('🔧 Firebase: Using FIREBASE_SERVICE_ACCOUNT_PATH')
      const serviceAccount = JSON.parse(readFileSync(envServiceAccountPath!, 'utf8'))
      const databaseURL =
        assertDatabaseUrl(process.env.FIREBASE_DATABASE_URL ?? serviceAccount.databaseURL)
      admin.initializeApp({
        credential: admin.credential.cert(serviceAccount),
        databaseURL
      })
    }
    // Strategy 1: Development with serviceAccount.json
    else if (isDev && hasServiceAccount) {
      const devPath = resolveDevServiceAccountPath()
      logger.info('🔧 Firebase: Using serviceAccount.json (development)', { path: devPath })
      const serviceAccount = JSON.parse(readFileSync(devPath, 'utf8'))
      const databaseURL = assertDatabaseUrl(process.env.FIREBASE_DATABASE_URL ?? serviceAccount.databaseURL)
      admin.initializeApp({
        credential: admin.credential.cert(serviceAccount),
        databaseURL
      })
    }
    // Strategy 2: Production with environment variables
    else if (process.env.FIREBASE_PROJECT_ID && process.env.FIREBASE_CLIENT_EMAIL && process.env.FIREBASE_PRIVATE_KEY) {
      logger.info('🔧 Firebase: Using environment variables')
      const privateKey = process.env.FIREBASE_PRIVATE_KEY.replaceAll(String.raw`\n`, '\n')
      const databaseURL = assertDatabaseUrl(process.env.FIREBASE_DATABASE_URL)
      
      admin.initializeApp({
        credential: admin.credential.cert({
          projectId: process.env.FIREBASE_PROJECT_ID,
          clientEmail: process.env.FIREBASE_CLIENT_EMAIL,
          privateKey
        }),
        databaseURL
      })
    }
    // Strategy 3: Production with GOOGLE_APPLICATION_CREDENTIALS
    else if (process.env.GOOGLE_APPLICATION_CREDENTIALS) {
      logger.info('🔧 Firebase: Using GOOGLE_APPLICATION_CREDENTIALS')
      admin.initializeApp({
        credential: admin.credential.applicationDefault(),
        databaseURL: assertDatabaseUrl(process.env.FIREBASE_DATABASE_URL)
      })
    }
    // Strategy 4: Cloud Run / GCP with default credentials
    else {
      logger.info('🔧 Firebase: Using default credentials (GCP)')
      admin.initializeApp({
        credential: admin.credential.applicationDefault(),
        databaseURL: assertDatabaseUrl(process.env.FIREBASE_DATABASE_URL)
      })
    }

    dbInstance = admin.firestore()
    authInstance = admin.auth()
    storageInstance = admin.storage()
    realtimeDbInstance = admin.database()
    
    // Cache Timestamp and FieldValue for exports
    timestampClass = admin.firestore.Timestamp
    fieldValueClass = admin.firestore.FieldValue

    initialized = true
    logger.info('✅ Firebase initialized successfully')
  } catch (err) {
    logger.error('❌ Firebase initialization failed', { error: err })
    throw new Error('Firebase credentials not found or invalid. Check environment variables or serviceAccount.json')
  }
}

// Export Firebase Admin helpers
export const getAuth = () => {
  ensureInitialized()
  return authInstance!
}

export const getFirestore = () => {
  ensureInitialized()
  return dbInstance!
}

export const getRealtimeDb = () => {
  ensureInitialized()
  return realtimeDbInstance!
}

export const verifyIdToken = (token: string) => {
  ensureInitialized()
  return authInstance!.verifyIdToken(token)
}

export const setCustomClaims = (uid: string, claims: object) => {
  ensureInitialized()
  return authInstance!.setCustomUserClaims(uid, claims)
}

// Lazy-loaded exports (only available after initialization)
export const getTimestamp = () => {
  ensureInitialized()
  return timestampClass!
}

export const getFieldValue = () => {
  ensureInitialized()
  return fieldValueClass!
}

// Legacy exports (backwards compatibility - will throw if accessed before init)
export const timestamp = new Proxy({}, {
  get(_target, prop, receiver) {
    ensureInitialized()
    const value = Reflect.get(timestampClass!, prop, receiver)
    return typeof value === 'function' ? value.bind(timestampClass) : value
  }
}) as typeof FirestoreTimestamp

export const FieldValue = new Proxy({}, {
  get(_target, prop) {
    ensureInitialized()
    return Reflect.get(fieldValueClass!, prop)
  }
}) as typeof FirestoreFieldValue
